// @flow

import React, { type Node, PureComponent } from 'react';
import LinkifyIt from 'linkify-it';
import NavigateLink from 'app/common/react/ui/NavigateLink';
import tlds from 'tlds';
import urlUtil from 'app/common/lib/urlUtil';

const linkify = new LinkifyIt();
linkify.tlds(tlds);

linkify.add('#', {
  validate(text, pos, self) {
    const tail = text.slice(pos);
    if (!self.re.hashtag) {
      self.re.hashtag = new RegExp('^([a-zA-Z0-9_]){1,15}(?!_)(?=$|' + self.re.src_ZPCc + ')');
    }
    if (self.re.hashtag.test(tail)) {
      // Linkifier allows punctuation chars before prefix,
      // but we additionally disable `#` ("##hashtag" is invalid)
      if (pos >= 2 && tail[pos - 2] === '#') {
        return false;
      }
      return tail.match(self.re.hashtag)[0].length;
    }
    return 0;
  },
  normalize(match) {
    match.url = `https://pinterest.com/search/pins/?q=${encodeURIComponent(
      match.text,
    )}&rs=hashtag_pinrep`;
  },
});

const matchDecorator = (text: string): Array<Object> => linkify.match(text);

const componentDecorator = (
  decoratedHref: string,
  decoratedText: string,
  key: number,
  isTrusted: boolean,
): Node => (
  <NavigateLink to={decoratedHref} stopPropagation target={!isTrusted} key={key}>
    {decoratedText}
  </NavigateLink>
);

type Props = {
  children: Node,
};

export default class Linkify extends PureComponent<Props> {
  parseString(string: string) {
    if (string === '') {
      return string;
    }
    const matches = matchDecorator(string);
    if (!matches) {
      return string;
    }

    const elements = [];
    let lastIndex = 0;
    matches.forEach((match, i) => {
      // Push preceding text if there is any
      if (match.index > lastIndex) {
        elements.push(string.substring(lastIndex, match.index));
      }
      let href = match.url;
      const isTrusted = urlUtil.isTrustedOrigin(match.url) && !match.url.match(/help.pinterest/g);
      if (isTrusted) {
        const parsed = urlUtil.parse(match.url);
        href = href.replace(parsed.origin, '');
      }
      const text = match.text;
      const decoratedComponent = componentDecorator(href, text, i, isTrusted);
      elements.push(decoratedComponent);

      lastIndex = match.lastIndex;
    });

    // Push remaining text if there is any
    if (string.length > lastIndex) {
      elements.push(string.substring(lastIndex));
    }

    return elements.length === 1 ? elements[0] : elements;
  }

  parse(children: any, key: number = 0) {
    if (typeof children === 'string') {
      return this.parseString(children);
    }
    if (React.isValidElement(children) && children.type !== 'a' && children.type !== 'button') {
      return React.cloneElement(children, { key }, this.parse(children.props.children));
    }
    if (Array.isArray(children)) {
      return children.map((child, i) => this.parse(child, i));
    }

    return children;
  }

  render(): Node {
    return <span>{this.parse(this.props.children)}</span>;
  }
}
